/**
 * Utilidades para cálculos de precios con/sin IVA en facturas
 */

const IVA_RATE = 0.19; // 19% IVA en Chile
const IVA_MULTIPLIER = 1 + IVA_RATE; // 1.19

/**
 * Redondea un número a 2 decimales
 */
export const redondear = (valor: number): number => {
  return Math.round(valor * 100) / 100;
};

/**
 * Calcula el precio sin IVA a partir de un precio con IVA
 * @param precioConIva Precio que incluye IVA
 * @returns Precio sin IVA (redondeado a 2 decimales)
 */
export const calcularPrecioSinIva = (precioConIva: number): number => {
  return redondear(precioConIva / IVA_MULTIPLIER);
};

/**
 * Calcula el precio con IVA a partir de un precio sin IVA
 * @param precioSinIva Precio base sin IVA
 * @returns Precio con IVA incluido (redondeado a 2 decimales)
 */
export const calcularPrecioConIva = (precioSinIva: number): number => {
  return redondear(precioSinIva * IVA_MULTIPLIER);
};

/**
 * Calcula el monto de IVA a partir de un precio sin IVA
 * @param precioSinIva Precio base sin IVA
 * @returns Monto del IVA (redondeado a 2 decimales)
 */
export const calcularMontoIva = (precioSinIva: number): number => {
  return redondear(precioSinIva * IVA_RATE);
};

/**
 * Convierte un precio según si incluye o no IVA
 * @param precio Precio original
 * @param incluyeIva Si el precio original incluye IVA
 * @returns Precio sin IVA (redondeado a 2 decimales)
 */
export const normalizarPrecioSinIva = (
  precio: number,
  incluyeIva: boolean
): number => {
  if (incluyeIva) {
    return calcularPrecioSinIva(precio);
  }
  return redondear(precio);
};

/**
 * Calcula los totales de una factura.
 *
 * Cuando los valores incluyen IVA se usa el método SII:
 *   1. Sumar todos los montos brutos (precio_con_iva × cantidad) → total bruto
 *   2. IVA = round(total_bruto × 19/119)  — una sola operación de redondeo
 *   3. Neto = total_bruto − IVA
 * Esto evita la acumulación de errores que ocurre al dividir cada precio
 * individualmente por 1.19.
 *
 * Cuando los valores NO incluyen IVA:
 *   1. Sumar precio_unitario × cantidad → neto
 *   2. IVA = round(neto × 0.19)
 *   3. Total = neto + IVA
 *
 * @param detalles Array de detalles con cantidad y precio_unitario
 * @param valoresIncluyenIva Si los precios unitarios incluyen IVA
 * @returns Objeto con subtotal (neto), iva y total — todos en pesos enteros
 */
export const calcularTotalesFactura = (
  detalles: Array<{
    cantidad: number;
    precio_unitario: number;
  }>,
  valoresIncluyenIva: boolean = false
): {
  subtotal: number;
  iva: number;
  total: number;
} => {
  if (valoresIncluyenIva) {
    // Método SII: sumar brutos primero, extraer IVA una sola vez al total
    const totalConIva = Math.round(
      detalles.reduce((sum, d) => sum + d.precio_unitario * d.cantidad, 0)
    );
    const iva = Math.round((totalConIva * 19) / 119);
    const subtotal = totalConIva - iva;
    return { subtotal, iva, total: totalConIva };
  }

  // Sin IVA: calcular neto, agregar IVA al final
  const subtotal = Math.round(
    detalles.reduce((sum, d) => sum + d.precio_unitario * d.cantidad, 0)
  );
  const iva = Math.round(subtotal * 0.19);
  const total = subtotal + iva;
  return { subtotal, iva, total };
};

/**
 * Recalcula el precio unitario de un detalle según el estado del checkbox de IVA
 * @param precioOriginal Precio original ingresado
 * @param ivaAnterior Estado anterior del checkbox (¿incluía IVA?)
 * @param ivaNuevo Estado nuevo del checkbox (¿incluye IVA?)
 * @returns Precio recalculado
 */
export const recalcularPrecioPorCambioIva = (
  precioOriginal: number,
  ivaAnterior: boolean,
  ivaNuevo: boolean
): number => {
  // Si no cambió el estado, retornar el mismo precio
  if (ivaAnterior === ivaNuevo) {
    return precioOriginal;
  }

  // Si cambió de "sin IVA" a "con IVA": agregar IVA
  if (!ivaAnterior && ivaNuevo) {
    return calcularPrecioConIva(precioOriginal);
  }

  // Si cambió de "con IVA" a "sin IVA": quitar IVA
  if (ivaAnterior && !ivaNuevo) {
    return calcularPrecioSinIva(precioOriginal);
  }

  return precioOriginal;
};

/**
 * Formatea un precio para mostrar sin decimales (para totales, subtotales, IVA)
 */
export const formatearPrecio = (precio: number): string => {
  return precio.toLocaleString('es-CL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

/**
 * Formatea un precio unitario mostrando hasta 2 decimales solo si son distintos de cero.
 * Ejemplos: 8.9 → "8,9" | 9.0 → "9" | 8.95 → "8,95" | 1234 → "1.234"
 */
export const formatearPrecioUnitario = (precio: number): string => {
  return precio.toLocaleString('es-CL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

/**
 * Formatea un precio con decimales si es necesario
 */
export const formatearPrecioConDecimales = (precio: number): string => {
  return precio.toLocaleString('es-CL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};
